import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  useTheme,
  Skeleton,
  Tooltip,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CustomCard from '../../components/@extended/CustomCard';
import { bloqcibeApi, useGetBalancingCriteriaConfigQuery } from '../../store/slices/apiSlice';
import { useSelector } from 'react-redux';
import { SUBJECT_STATUSES } from '../../util/constants';

// Default statuses to exclude
const DEFAULT_EXCLUDE_STATUSES = [
  SUBJECT_STATUSES.SCREENING_FAILURE,
  SUBJECT_STATUSES.ENROLLMENT_FAILURE,
  SUBJECT_STATUSES.WITHDRAWAL,
  SUBJECT_STATUSES.ON_HOLD,
];

/**
 * BalancingCriteriaItem - Displays a single balancing criteria with progress bar
 * 
 * CONSISTENCY NOTES (aligned with backend subject.service.ts and DynamicForm.jsx):
 * - Uses Math.floor() for percentage to absolute number conversion
 * - Uses strict greater than (>) for comparison (to match backend ON_HOLD trigger)
 * - Handles non-percentage distribution as absolute number
 * - Default distribution is '%' if not specified
 * 
 * @param {string} label - Display label for the criteria
 * @param {number} current - Current count of subjects for this criteria
 * @param {number} target - Target value (percentage if distribution is '%', absolute otherwise)
 * @param {string} distribution - Distribution type ('%' for percentage, anything else for absolute)
 * @param {number} total - Total target subjects for the site
 * @param {boolean} showMaximum - Whether to show maximum limit (default: true)
 */
const BalancingCriteriaItem = ({ label, current, target, distribution, total, showMaximum = true, unitLabel = null }) => {
  const theme = useTheme();
  
  // Target value from config
  const targetValue = target || 0;
  
  // Calculate target number based on distribution type
  // Uses Math.floor() for consistency with backend
  // If distribution is '%', convert percentage to absolute number
  // Otherwise, use the target value directly as an absolute number
  const isPercentageDistribution = distribution === '%';
  const targetNumber = isPercentageDistribution
    ? (total > 0 ? Math.floor((targetValue / 100) * total) : 0)
    : targetValue;
  
  // Calculate progress percentage  based on target number (not total)
  // This shows how close we are to reaching the maximum allowed for this criteria
  const progressPercentage = targetNumber > 0 ? (current / targetNumber) * 100 : 0;
  
  // Calculate percentage of current out of total (for display purposes)
  const percentageOfTotal = total > 0 ? (current / total) * 100 : 0;
  
  // Check if exceeded the target
  // Uses strict greater than (>) for consistency with backend ON_HOLD trigger logic
  const isExceeded = showMaximum && current > targetNumber && targetNumber > 0;
  
  // Determine chip color based on showMaximum
  const getChipColor = () => {
    if (!showMaximum) {
      return {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
      };
    }
    return {
      backgroundColor: isExceeded ? theme.palette.error.light : theme.palette.primary.light,
      color: isExceeded ? theme.palette.error.contrastText : theme.palette.primary.contrastText,
    };
  };

  // Determine progress bar color based on showMaximum
  const getProgressBarColor = () => {
    if (!showMaximum) {
      return theme.palette.primary.main;
    }
    if (isExceeded) {
      return theme.palette.error.main;
    }
    if (progressPercentage >= 80) {
      return theme.palette.warning.main;
    }
    return theme.palette.primary.main;
  };

  const chipColors = getChipColor();
  
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Current: ${current} subjects${showMaximum ? ` (${percentageOfTotal.toFixed(1)}% of total)` : ` / ${total}`}`}>
            <Chip
              label={showMaximum ? `${current}` : `${current} / ${total}`}
              size="small"
              sx={{
                ...chipColors,
                fontWeight: 600,
                minWidth: 40,
              }}
            />
          </Tooltip>
          {showMaximum && targetNumber > 0 && (
            <Tooltip title={isPercentageDistribution 
              ? `Target: ${targetValue}${distribution} of ${total} subjects = ${targetNumber} max`
              : `Target: ${targetNumber} subjects (absolute)`
            }>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                / {targetNumber}
              </Typography>
            </Tooltip>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={showMaximum ? Math.min(progressPercentage, 100) : (total > 0 ? (current / total) * 100 : 0)}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: theme.palette.grey[200],
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            backgroundColor: getProgressBarColor(),
          },
        }}
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {current} / {showMaximum ? targetNumber : total}
        </Typography>
        {showMaximum && targetNumber > 0 && (
          <Tooltip title={isPercentageDistribution 
            ? `${targetValue}${distribution} of total enrollment`
            : `Maximum ${targetNumber} subjects (absolute limit)`
          }>
            {!unitLabel && <Typography variant="caption" sx={{ color: theme.palette.text.secondary, cursor: 'help' }}>
              Max: {isPercentageDistribution ? `${targetValue}${distribution}` : targetNumber}
            </Typography>}
          </Tooltip>
        )}
        {
          unitLabel && <Typography variant="caption" sx={{ color: theme.palette.text.secondary, cursor: 'help' }}>
            {unitLabel}
          </Typography>
        }
      </Box>
    </Box>
  );
};

const BalancingCriteriaCard = ({ criteriaKey, criteriaLabel, fields, aggregatedData, totalSubjects }) => {
  const theme = useTheme();
  
  // Create a map of aggregated data for quick lookup, keyed by fieldKey
  const countsMapByField = useMemo(() => {
    const map = {};
    if (aggregatedData) {
      aggregatedData.forEach((fieldData) => {
        const fieldKey = fieldData.fieldKey;
        if (!map[fieldKey]) {
          map[fieldKey] = {};
        }
        if (fieldData.counts) {
          fieldData.counts.forEach(({ value, subjectCount }) => {
            map[fieldKey][value] = subjectCount;
          });
        }
      });
    }
    return map;
  }, [aggregatedData]);

  // Check if fields have labels (multi-field mode like VDZ_DISCONTINUATION)
  const hasLabeledFields = useMemo(() => {
    return fields?.some((field) => field.label);
  }, [fields]);
  
  // Get all values from the field config
  const criteriaItems = useMemo(() => {
    if (!fields || fields.length === 0) return [];
    
    const items = [];

    if (hasLabeledFields) {
      // Multi-field mode: Group by field label
      // Sum up all option counts for each labeled field
      fields.forEach((field) => {
        if (field.label && field.values) {
          const fieldCounts = countsMapByField[field.fieldKey] || {};
          
          // Sum all option counts for this field
          let totalCount = 0;
          field.values.forEach((valueConfig) => {
            totalCount += fieldCounts[valueConfig.option] || 0;
          });

          // Use the first value's target and distribution (they should be the same for labeled fields)
          const firstValue = field.values[0];
          items.push({
            label: field.label,
            current: totalCount,
            target: firstValue?.value || 0,
            // Fallback order: distribution → disribution (typo) → '%' (consistent with backend)
            distribution: firstValue?.distribution || firstValue?.disribution || '%',
            showMaximum: firstValue?.showMaximum !== false, // Default to true if not specified
            unitLabel: field.unitLabel || firstValue?.unitLabel || null,
          });
        }
      });
    } else {
      // Single-field mode: Show each option separately
      fields.forEach((field) => {
        const fieldCounts = countsMapByField[field.fieldKey] || {};
        if (field.values) {
          field.values.forEach((valueConfig) => {
            items.push({
              label: valueConfig.label || valueConfig.option,
              current: fieldCounts[valueConfig.option] || 0,
              target: valueConfig.value,
              // Fallback order: distribution → disribution (typo) → '%' (consistent with backend)
              distribution: valueConfig.distribution || valueConfig.disribution || '%',
              showMaximum: valueConfig.showMaximum !== false, // Default to true if not specified
              unitLabel: field.unitLabel || valueConfig.unitLabel || null,
            });
          });
        }
      });
    }
    
    return items;
  }, [fields, countsMapByField, hasLabeledFields]);

  // Format title from key if no label provided
  const formatTitleFromKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Use criteriaLabel if provided, otherwise format the criteriaKey
  const displayTitle = criteriaLabel || formatTitleFromKey(criteriaKey);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        p: 2,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: theme.palette.primary.main,
          mb: 2,
          pb: 1,
          borderBottom: `2px solid ${theme.palette.primary.light}`,
        }}
      >
        {displayTitle}
      </Typography>
      {criteriaItems.map((item, index) => (
        <BalancingCriteriaItem
          key={`${criteriaKey}-${item.label}-${index}`}
          label={item.label}
          current={item.current}
          target={item.target}
          distribution={item.distribution}
          total={totalSubjects}
          showMaximum={item.showMaximum}
          unitLabel={item?.unitLabel || null}
        />
      ))}
    </Box>
  );
};

const BalancingCriteriaWidget = ({ trialId, siteId, documentKey }) => {
  const theme = useTheme();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  const totalSubjects = useSelector(
    (state) => state?.trial?.trialSiteDetail?.siteTrialData?.totalSubjects || 0
  );
  
  const [getSubjectCountByField] = bloqcibeApi.endpoints.getSubjectCountByField.useLazyQuery();
  const [aggregatedData, setAggregatedData] = useState({});
  const [isLoadingAggregations, setIsLoadingAggregations] = useState(false);
  
  // Status filter state - tracks which statuses to INCLUDE
  // By default, failure statuses and ON_HOLD are NOT included (unchecked)
  const [showFilters, setShowFilters] = useState(false);
  const [includeStatuses, setIncludeStatuses] = useState({
    // CRF Pending statuses - included by default
    [SUBJECT_STATUSES.PENDING]: true,
    [SUBJECT_STATUSES.PENDING_VERIFICATION]: true,
    [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: true,

    [SUBJECT_STATUSES.COMPLETED]: true,
    // Failure and hold statuses - excluded by default
    [SUBJECT_STATUSES.ON_HOLD]: false,
    [SUBJECT_STATUSES.SCREENING_FAILURE]: false,
    [SUBJECT_STATUSES.ENROLLMENT_FAILURE]: false,
    [SUBJECT_STATUSES.WITHDRAWAL]: false,
  });

  // Helper to handle grouped CRF Pending filter toggle
  const handleCRFPendingFilterChange = useCallback(() => {
    setIncludeStatuses((prev) => {
      const newValue = !(prev[SUBJECT_STATUSES.PENDING] && 
                         prev[SUBJECT_STATUSES.PENDING_VERIFICATION] && 
                         prev[SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]);
      return {
        ...prev,
        [SUBJECT_STATUSES.PENDING]: newValue,
        [SUBJECT_STATUSES.PENDING_VERIFICATION]: newValue,
        [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: newValue,
      };
    });
  }, []);

  // Check if all CRF Pending statuses are included
  const isCRFPendingIncluded = useMemo(() => {
    return includeStatuses[SUBJECT_STATUSES.PENDING] && 
           includeStatuses[SUBJECT_STATUSES.PENDING_VERIFICATION] && 
           includeStatuses[SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION];
  }, [includeStatuses]);

  // Fetch balancing criteria config
  const { data: configData, isLoading: isLoadingConfig } = useGetBalancingCriteriaConfigQuery(
    { type: 'SUBJECT_BALANCING_CRITERIA', documentKey },
    { skip: !documentKey }
  );

  // Get the appropriate config (site-specific or default)
  const activeConfig = useMemo(() => {
    if (!configData?.metaData) return null;
    
    const metaData = configData.metaData;
    // Check for site-specific config first, then fall back to default
    return metaData[siteId] || metaData.default || null;
  }, [configData, siteId]);

  // Build status filter for API call
  // Statuses that are NOT checked (includeStatuses[status] === false) should be excluded
  const statusFilter = useMemo(() => {
    const excludeStatusArray = Object.entries(includeStatuses)
      .filter(([_, isIncluded]) => !isIncluded)
      .map(([status]) => status);
    
    return excludeStatusArray.length > 0 
      ? { excludeStatuses: excludeStatusArray } 
      : undefined;
  }, [includeStatuses]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((status) => {
    setIncludeStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  }, []);

  // Fetch aggregated subject counts for each criteria
  useEffect(() => {
    const fetchAggregations = async () => {
      if (!activeConfig || !sponsorId || !trialId || !siteId) return;

      setIsLoadingAggregations(true);
      const aggregations = {};

      try {
        const criteriaKeys = Object.keys(activeConfig);
        
        for (const criteriaKey of criteriaKeys) {
          const criteriaConfig = activeConfig[criteriaKey];
          if (!criteriaConfig?.fields) continue;

          const fieldsToQuery = criteriaConfig.fields.map((field) => ({
            stepKey: configData.stepKey,
            sectionKey: configData.sectionKey,
            subSectionKey: configData.subSectionKey || null,
            categoryKey: field.categoryKey,
            fieldKey: field.fieldKey,
          }));

          try {
            const result = await getSubjectCountByField({
              sponsorId,
              trialId,
              siteId,
              body: { 
                fields: fieldsToQuery,
                statusFilter: statusFilter,
              },
            }).unwrap();

            aggregations[criteriaKey] = result;
          } catch (error) {
            console.error(`Error fetching aggregation for ${criteriaKey}:`, error);
            aggregations[criteriaKey] = [];
          }
        }

        setAggregatedData(aggregations);
      } catch (error) {
        console.error('Error fetching aggregations:', error);
      } finally {
        setIsLoadingAggregations(false);
      }
    };

    fetchAggregations();
  }, [activeConfig, sponsorId, trialId, siteId, configData, getSubjectCountByField, statusFilter]);

  // Default filter values - used to determine if filters have been changed
  const defaultFilterValues = useMemo(() => ({
    [SUBJECT_STATUSES.PENDING]: true,
    [SUBJECT_STATUSES.PENDING_VERIFICATION]: true,
    [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: true,
    [SUBJECT_STATUSES.COMPLETED]: true,
    [SUBJECT_STATUSES.ON_HOLD]: false,
    [SUBJECT_STATUSES.SCREENING_FAILURE]: false,
    [SUBJECT_STATUSES.ENROLLMENT_FAILURE]: false,
    [SUBJECT_STATUSES.WITHDRAWAL]: false,
  }), []);

  // Count how many filters differ from their default values
  const changedFilterCount = useMemo(() => {
    return Object.entries(includeStatuses).filter(
      ([status, isIncluded]) => isIncluded !== defaultFilterValues[status]
    ).length;
  }, [includeStatuses, defaultFilterValues]);

  if (!documentKey) {
    return null;
  }

  if (isLoadingConfig) {
    return (
      <CustomCard title="Subject Balancing Criteria">
        <Box sx={{ p: 2 }}>
          <Skeleton variant="rectangular" height={200} />
        </Box>
      </CustomCard>
    );
  }

  if (!activeConfig || Object.keys(activeConfig).length === 0) {
    return null;
  }

  const criteriaKeys = Object.keys(activeConfig);

  // Filter action button for CustomCard
  const filterAction = (
    <Tooltip title="Filter by subject status">
      <IconButton
        size="small"
        onClick={() => setShowFilters(!showFilters)}
        sx={{
          color: changedFilterCount > 0 ? theme.palette.primary.main : theme.palette.text.secondary,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <FilterListIcon />
        {changedFilterCount > 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 0,
              right: 0,
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: '50%',
              width: 16,
              height: 16,
              fontSize: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {changedFilterCount}
          </Box>
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <CustomCard title="Subject Balancing Criteria" action={filterAction}>
      <Box sx={{ p: 1 }}>
        {/* Status Filter Controls */}
        <Collapse in={showFilters}>
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              backgroundColor: theme.palette.grey[50],
              borderRadius: 1,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, mb: 1, display: 'block' }}>
              Include subjects with status:
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isCRFPendingIncluded}
                    onChange={handleCRFPendingFilterChange}
                  />
                }
                label={
                  <Tooltip title="Includes Pending, PI Verification Pending, and Site Monitor Verification Pending statuses">
                    <Typography variant="body2">Incomplete Subject forms</Typography>
                  </Tooltip>
                }
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeStatuses[SUBJECT_STATUSES.ON_HOLD]}
                    onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.ON_HOLD)}
                  />
                }
                label={<Typography variant="body2">On Hold</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeStatuses[SUBJECT_STATUSES.SCREENING_FAILURE]}
                    onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.SCREENING_FAILURE)}
                  />
                }
                label={<Typography variant="body2">Screening Failed</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeStatuses[SUBJECT_STATUSES.ENROLLMENT_FAILURE]}
                    onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.ENROLLMENT_FAILURE)}
                  />
                }
                label={<Typography variant="body2">Eligibility Failed</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeStatuses[SUBJECT_STATUSES.WITHDRAWAL]}
                    onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.WITHDRAWAL)}
                  />
                }
                label={<Typography variant="body2">Withdrawn</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={includeStatuses[SUBJECT_STATUSES.COMPLETED]}
                    onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.COMPLETED)}
                  />
                }
                label={<Typography variant="body2">Completed</Typography>}
              />
            </FormGroup>
          </Box>
        </Collapse>

        {isLoadingAggregations ? (
          <Grid container spacing={2}>
            {criteriaKeys.map((key) => (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {criteriaKeys.map((criteriaKey) => (
              <Grid item xs={12} sm={6} md={4} key={criteriaKey}>
                <BalancingCriteriaCard
                  criteriaKey={criteriaKey}
                  criteriaLabel={activeConfig[criteriaKey]?.label}
                  fields={activeConfig[criteriaKey]?.fields || []}
                  aggregatedData={aggregatedData[criteriaKey] || []}
                  totalSubjects={totalSubjects}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </CustomCard>
  );
};

export default BalancingCriteriaWidget;

